import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

import { ApplicationType, PermissionFullApiModel, PermissionPostApiModel, PermissionsCheckMethod } from '@kros-sk/models';

import { APPLICATION_TYPE_CONFIG } from '../../auth/kros-auth.config';
import { AuthSelectorsService } from '../../auth/store/auth-selectors.service';
import { PermissionsDataService } from './permissions-data.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionsStore {
  private readonly permissionsDataService = inject(PermissionsDataService);
  private readonly authSelectorsService = inject(AuthSelectorsService);

  private permissionsLoaded = false;
  private companyId = -1;
  private version = 0;

  private permissionsLoadedSubject = new BehaviorSubject<boolean>(this.permissionsLoaded);
  private permissionsChangedSubject = new BehaviorSubject<number>(this.version);
  private reloadTriggerSubject = new BehaviorSubject<PermissionsLoadingParams>({ permissions: [], companyId: this.companyId });

  readonly permissionsChanged$ = this.permissionsChangedSubject.asObservable();
  readonly permissionsLoaded$ = this.permissionsLoadedSubject.asObservable();

  private cachedPermissions = new Map<string, boolean>();
  private readonly alreadyLoadedPermissions = new Set<string>();

  constructor() {
    this.reloadTriggerSubject
      .asObservable()
      .pipe(
        mergeMap((params) => combineLatest([of(params), this.authSelectorsService.isLoggedIn$])),
        filter(([params, isLoggedIn]) => isLoggedIn && params && params.companyId > 0 && params.permissions.length > 0),
        switchMap(([params, _]) => this.permissionsDataService.loadPermissions(params.companyId, params.permissions))
      )
      .subscribe((loadedPermissions) => {
        this.addLoadedPermissionsToMap(loadedPermissions);
        if (loadedPermissions && loadedPermissions.length > 0) {
          this.changeVersion();
        }
        this.setLoaded(true);
      });
  }

  loadPermissions(companyId: number, permissionsToLoad: Required<PermissionPostApiModel>[]): void {
    if (this.companyId !== companyId) {
      this.clearLoadedPermissions();
      this.companyId = companyId;
    }

    if (this.companyId === 0 && permissionsToLoad.length === 0) {
      // it's only a reset, not really loading of any permissions, but set successfully loaded state
      // otherwise guard stays locked
      this.changeVersion();
      this.setLoaded(true);
    }

    const notAlreadyLoadedPermissions = this.filterAlreadyLoadedPermissions(permissionsToLoad);
    if (!notAlreadyLoadedPermissions.length) {
      // all requested permissions have been already loaded
      return;
    }

    this.saveToLoadedPermissions(notAlreadyLoadedPermissions);

    this.setLoaded(false);
    this.reloadTriggerSubject.next({
      companyId: this.companyId,
      permissions: notAlreadyLoadedPermissions,
    });
  }

  clearLoadedPermissions(): void {
    this.cachedPermissions.clear();
    this.alreadyLoadedPermissions.clear();

    if (this.version) {
      this.setLoaded(false);
      this.changeVersion();
    }
  }

  seedLoadedPermissions(companyId: number, permissions: PermissionFullApiModel[]): void {
    if (this.companyId !== companyId) {
      this.clearLoadedPermissions();
      this.companyId = companyId;
    }

    for (const permission of permissions) {
      this.alreadyLoadedPermissions.add(
        this.formatApiPermissionAsAKey(permission.resourcePath, permission.action, permission.applicationType)
      );
    }
    this.addLoadedPermissionsToMap(permissions);
    this.changeVersion();
    this.setLoaded(true);
  }

  hasPermission(resourcePath: string, action: string, applicationType: ApplicationType): boolean {
    const key = this.formatApiPermissionAsAKey(resourcePath, action, applicationType);
    if (this.cachedPermissions.has(key)) {
      return this.cachedPermissions.get(key);
    }
    return false;
  }

  isPermissionLoaded(resourcePath: string, action: string, applicationType: ApplicationType): boolean {
    return this.alreadyLoadedPermissions.has(this.formatApiPermissionAsAKey(resourcePath, action, applicationType));
  }

  private changeVersion(): void {
    this.version++;
    this.permissionsChangedSubject.next(this.version);
  }

  private setLoaded(value: boolean): void {
    this.permissionsLoaded = value;
    this.permissionsLoadedSubject.next(this.permissionsLoaded);
  }

  private addLoadedPermissionsToMap(loadedPermissions: PermissionFullApiModel[]): void {
    if (loadedPermissions.length === 0) {
      return;
    }

    let key: string;
    for (const permission of loadedPermissions) {
      key = this.formatApiPermissionAsAKey(permission.resourcePath, permission.action, permission.applicationType);
      this.cachedPermissions = this.cachedPermissions.set(key, permission.isAllowed);
    }
  }

  protected formatApiPermissionAsAKey(resourcePath: string, action: string, applicationType: ApplicationType): string {
    return `${resourcePath}|${action}|${applicationType.toString()}`;
  }

  private filterAlreadyLoadedPermissions(newPermissions: Required<PermissionPostApiModel>[]): PermissionPostApiModel[] {
    const notAlreadyLoadedPermissions: PermissionPostApiModel[] = [];
    for (const permission of newPermissions) {
      if (!this.isPermissionLoaded(permission.resourcePath, permission.action, permission.applicationType)) {
        notAlreadyLoadedPermissions.push(permission);
      }
    }

    return notAlreadyLoadedPermissions;
  }

  private saveToLoadedPermissions(newPermissions: PermissionPostApiModel[]): void {
    for (const permission of newPermissions) {
      this.alreadyLoadedPermissions.add(
        this.formatApiPermissionAsAKey(permission.resourcePath, permission.action, permission.applicationType)
      );
    }
  }
}

interface PermissionsLoadingParams {
  companyId: number;
  permissions: PermissionPostApiModel[];
}

@Injectable({
  providedIn: 'any',
})
export class PermissionsService {
  private readonly currentApplicationType = inject<ApplicationType>(APPLICATION_TYPE_CONFIG);
  private readonly permissionsStore = inject(PermissionsStore);

  readonly permissionsChanged$ = this.permissionsStore.permissionsChanged$;
  readonly permissionsLoaded$ = this.permissionsStore.permissionsLoaded$;

  loadPermissions(companyId: number, permissionsToLoad: PermissionPostApiModel[]): void {
    const permissions = permissionsToLoad.map((permission) => ({
      ...permission,
      applicationType: permission.applicationType ?? this.currentApplicationType,
    }));

    this.permissionsStore.loadPermissions(companyId, permissions);
  }

  clearLoadedPermissions(): void {
    this.permissionsStore.clearLoadedPermissions();
  }

  seedLoadedPermissions(companyId: number, permissions: PermissionFullApiModel[]): void {
    this.permissionsStore.seedLoadedPermissions(companyId, permissions);
  }

  hasPermission(resourcePath: string, action: string, applicationType?: ApplicationType): boolean {
    return this.permissionsStore.hasPermission(resourcePath, action, applicationType ?? this.currentApplicationType);
  }

  hasPermissions(permissionsToCheck: PermissionPostApiModel[], checkMethod: PermissionsCheckMethod): boolean {
    if (!permissionsToCheck.length) {
      return true;
    }

    return this.checkPermissions(permissionsToCheck, checkMethod, (permission) =>
      this.hasPermission(permission.resourcePath, permission.action, permission.applicationType)
    );
  }

  private checkPermissions(
    permissionsToCheck: PermissionPostApiModel[],
    checkMethod: PermissionsCheckMethod,
    predicate: (permission: PermissionPostApiModel) => boolean
  ): boolean {
    if (checkMethod === PermissionsCheckMethod.AllGranted) {
      return permissionsToCheck.every((permission) => predicate(permission));
    }

    if (checkMethod === PermissionsCheckMethod.SomeGranted) {
      return permissionsToCheck.some((permission) => predicate(permission));
    }

    throw new Error(`Unknown permissions check method: "${checkMethod}"`);
  }
}
